/**
 * API Client
 * ==========
 * Axios client with interceptors for authentication and error handling
 */

import axios from 'axios';

// API base URL from environment or default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
const TOKEN_KEY = 'zamanat_access_token';
const REFRESH_TOKEN_KEY = 'zamanat_refresh_token';

export const tokenManager = {
  getAccessToken: () => localStorage.getItem(TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors and token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    // Don't modify the response structure - let individual API functions handle it
    // The backend returns different structures:
    // - Login: { access_token, refresh_token, user, ... }
    // - Users list: { data: [...], pagination: {...}, meta: {...} }
    // - Single user: { data: {...}, meta: {...} }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      return Promise.reject({
        code: 'NETWORK_ERROR',
        message: 'There is some network related issue while connecting to the Internet. Please switch your connection to some alternate connectivity provider and try again',
        details: {},
      });
    }

    // Extract error from standardized error response
    // Backend returns: {detail: {error: {code, message, details}}} or {detail: "string"}
    let errorData;
    if (typeof error.response.data?.detail === 'string') {
      // Handle simple string detail from FastAPI HTTPException
      errorData = {
        code: 'API_ERROR',
        message: error.response.data.detail,
        details: {},
      };
    } else {
      errorData = error.response.data?.detail?.error || error.response.data?.error || {
        code: 'UNKNOWN_ERROR',
        message: error.message,
        details: {},
      };
    }

    // Handle 401 Unauthorized - Token expired
    if (error.response.status === 401 && !originalRequest._retry) {
      if (errorData.code === 'TOKEN_EXPIRED') {
        if (isRefreshing) {
          // Queue the request while token is being refreshed
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return apiClient(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = tokenManager.getRefreshToken();
        if (!refreshToken) {
          // No refresh token, redirect to login
          tokenManager.clearTokens();
          window.location.href = '/login';
          return Promise.reject(errorData);
        }

        try {
          // Attempt to refresh token
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            { refresh_token: refreshToken }
          );

          const newAccessToken = response.data.data.access_token;
          tokenManager.setTokens(newAccessToken, refreshToken);

          // Update authorization header
          apiClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          // Process queued requests
          processQueue(null, newAccessToken);

          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          processQueue(refreshError, null);
          tokenManager.clearTokens();
          window.location.href = '/login';
          return Promise.reject(errorData);
        } finally {
          isRefreshing = false;
        }
      } else if (
        errorData.code === 'INVALID_TOKEN' ||
        errorData.code === 'SESSION_REVOKED' ||
        errorData.code === 'INVALID_SESSION'
      ) {
        // Invalid token or session errors - clear tokens and redirect
        tokenManager.clearTokens();
        window.location.href = '/login';
        return Promise.reject(errorData);
      }
      // For other 401 errors (like INVALID_CREDENTIALS), just reject without redirect
      // This allows the login form to display the error message
    }

    // Handle 403 Forbidden
    if (error.response.status === 403) {
      // Could show a permission denied message
      console.error('Permission denied:', errorData.message);
    }

    // Handle 422 Validation Errors
    if (error.response.status === 422) {
      // For Pydantic validation errors, extract field errors
      if (error.response.data?.detail && Array.isArray(error.response.data.detail)) {
        const fieldErrors = {};
        error.response.data.detail.forEach((err) => {
          const field = err.loc?.[err.loc.length - 1] || 'unknown';
          fieldErrors[field] = err.msg;
        });
        return Promise.reject({
          code: 'VALIDATION_ERROR',
          message: error.response.data.detail[0]?.msg || error.message,
          details: fieldErrors,
        });
      }
    }

    return Promise.reject(errorData);
  }
);

export default apiClient;

